import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { KycDocLoader } from "../../../components/Onboarding/KycDocLoader";
import { Button, Icon, Modal } from "../../../components/design";
import useTranslator from "../../../hooks/useTranslator";
import KycVerificationPopup from "../KycVerificationPopup";
import { COUNTRIES, getDocumentsForCountry } from "./documentSelection";
import { KYC_STEP, useKycWorkflow } from "./hooks/useKycWorkflow";

import styles from "./authenticateYourId.module.css";
import ContinueOnPhoneScreen from "./components/ContinueOnPhoneScreen";
import DocumentSelectScreen from "./components/DocumentSelectScreen";
import FileUploadScreen from "./components/FileUploadScreen";
import LoadingScreen from "./components/LoadingScreen";
import ProcessingScreen from "./components/ProcessingScreen";
import VerificationFailedModal from "./components/VerificationFailed";
import VerificationStartScreen from "./components/VerificationStartScreen";
import ExistingVerificationScreen from "./components/ExistingVerificationScreen";
import CaptureScreen from "../../mobile-capture/components/CaptureScreen";
import GuideScreen from "../../mobile-capture/components/GuideScreen";
import KycVerificationFrame from "../../../components/Kyc/KycVerificationFrame";
import kycFrameStyles from "../../../components/Kyc/KycVerificationFrame.module.css";

/** Redo KYC uses the shared portal so page navigation cannot cover the dialog. */
const KycFlowSurface = ({
  popup,
  welcome = false,
  isGuarantor,
  onClose,
  fillFrame = false,
  children,
}: {
  popup: boolean;
  welcome?: boolean;
  isGuarantor: boolean;
  onClose: () => void;
  // Didit KYC iframe on screen: the surface fits the screen and the iframe
  // takes its full height (full-screen layout only).
  fillFrame?: boolean;
  children: ReactNode;
}) => {
  if (popup) {
    return (
      <Modal open onClose={onClose} dismissible={false} size="wide">
        <div className={`${styles.popupFlow} notranslate`} translate="no">
          {children}
        </div>
      </Modal>
    );
  }

  return (
    <div
      className={`${styles.overlay} wrapper-modal notranslate ${
        welcome
          ? `approvedModal popup-overlay modal-fixed-screen ${isGuarantor ? "guarantorBg" : ""}`
          : "onfido-overlay"
      }`}
      style={welcome ? { zIndex: 99 } : undefined}
      translate="no"
    >
      <div
        className={`${styles.authModalSurface} ${
          welcome
            ? "popup-content2 position-relative popup-content bg-white required-doc-popup authenticate-your-id-welcome"
            : "onfido-wrapper"
        } ${fillFrame ? kycFrameStyles.hostWrapper : ""}`}
        onClick={
          welcome && isGuarantor ? (e) => e.stopPropagation() : undefined
        }
      >
        {children}
      </div>
    </div>
  );
};

interface AuthenticateYourIdProps {
  user: { _id: string; [key: string]: any };
  formHandler: { prev: () => void };
  updateUserProfile: (data: any) => void;
  isGuarantor: boolean;
  isCoTenant?: boolean;
  usedFor: string;
  setShowKycPopup: (show: boolean) => void;
  addedFiles: any[];
  setAddedFiles: (files: any[]) => void;
  getNextStep: () => number;
  isKycSkippable?: boolean;
  onSkipKyc?: () => void;
  isTenantCheck?: boolean;
  hideBackButton?: boolean;
  onProgressChange?: (stage: "document" | "photos" | "processing") => void;
}

const AuthenticateYourId = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  isCoTenant = false,
  usedFor,
  setShowKycPopup,
  setAddedFiles,
  getNextStep,
  isKycSkippable = false,
  onSkipKyc,
  onProgressChange,
  isTenantCheck,
  hideBackButton,
}: AuthenticateYourIdProps) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const [showVerificationStart, setShowVerificationStart] = useState(false);
  const isMobileOrTablet = useMediaQuery({ maxWidth: 1024 });

  const {
    failedKyc,
    setFailedKyc,
    failedWorkflowResult,
    customKycStarted,
    kycInitiateLoading,
    kycInitiationFailed,
    kycVerificationPopup,
    showKycLoading,
    showDocumentRetryMessage,
    kycStep,
    docType,
    issuingCountry,
    frontImage,
    setFrontImage,
    backImage,
    setBackImage,
    captureSubMode,
    setCaptureSubMode,
    needsBackSide,
    startCustomKyc,
    startHostedKyc,
    handleHostedKycFinished,
    kycSessionUrl,
    handleStartVerification,
    handleCreateMobileSession,
    handleCustomKycCancel,
    handleDocTypeChange,
    handleCountryChange,
    handleDocTypeSelect,
    handleFileUploadFront,
    handleFileUploadBack,
    handleFrontCapture,
    handleBackCapture,
    handleMobileCancel,
    mobileSessionData,
    mobileSessionState,
    handleKycContinue,
    setKycStep,
  } = useKycWorkflow({
    user,
    isGuarantor,
    isCoTenant,
    usedFor,
    setShowKycPopup,
    setAddedFiles,
    getNextStep,
    updateUserProfile,
  });

  useEffect(() => {
    if (!onProgressChange) return;

    if (
      kycStep === KYC_STEP.UPLOADING ||
      kycStep === KYC_STEP.PROCESSING ||
      (kycStep === KYC_STEP.CONTINUE_ON_PHONE &&
        mobileSessionState === "SUBMITTED")
    ) {
      onProgressChange("processing");
    } else if (
      kycStep === KYC_STEP.CAPTURE_FRONT ||
      kycStep === KYC_STEP.CAPTURE_BACK ||
      kycStep === KYC_STEP.CONTINUE_ON_PHONE
    ) {
      onProgressChange("photos");
    } else {
      onProgressChange("document");
    }
  }, [kycStep, mobileSessionState, onProgressChange]);

  const getTranslatedText = (key: string, fallback: string) => {
    return _t(key);
  };

  const getDocumentUploadTitle = (side: "front" | "back") => {
    if (docType === "passport") {
      return getTranslatedText("uploadYourPassport", "Upload your passport");
    }
    if (docType === "residence_permit") {
      if (side === "back") {
        return getTranslatedText(
          "uploadYourBackSideOfResidencePermit",
          "Upload the back side of your residence permit"
        );
      }
      return getTranslatedText(
        "uploadYourResidencePermit",
        "Upload your residence permit"
      );
    }
    if (side === "back") {
      return getTranslatedText(
        "uploadYourBackSideOfNationalIdentityCard",
        "Upload the back side of your ID"
      );
    }
    return getTranslatedText(
      "uploadYourNationalIdentityCard",
      "Upload your ID"
    );
  };

  const getDocumentUploadSubtitle = (side: "front" | "back") => {
    if (docType === "passport") {
      return getTranslatedText(
        "passportPhotoPageHint",
        "We need the photo page, complete and legible"
      );
    }
    if (side === "back") {
      return getTranslatedText(
        "nowUploadDocumentBack",
        "Now upload the back of your document."
      );
    }
    return getTranslatedText(
      "startWithDocumentFront",
      "Start with the front. We'll ask for the back later."
    );
  };

  const renderCaptureStep = ({
    title,
    subtitle,
    side,
    onFileUpload,
    onContinueOnPhone,
    onTakePhoto,
  }: {
    title: string;
    subtitle: string;
    side: "front" | "back";
    onFileUpload: (file: File | Blob) => Promise<void>;
    onContinueOnPhone?: () => void;
    onTakePhoto?: () => void;
  }) => {
    return (
      <FileUploadScreen
        key={kycStep}
        title={title}
        subtitle={subtitle}
        docType={docType}
        side={side}
        showStepBadge={needsBackSide}
        onFileSelect={onFileUpload}
        onContinueOnPhone={onContinueOnPhone}
        onTakePhoto={onTakePhoto}
      />
    );
  };

  // Hosted (Didit) flow: the iframe, then a processing screen while the result
  // arrives. Back / close returns to the start screen.
  const renderHostedKycStep = () => {
    if (kycStep === KYC_STEP.PROCESSING) {
      return <ProcessingScreen />;
    }
    return (
      <KycVerificationFrame
        url={kycSessionUrl}
        onFinished={handleHostedKycFinished}
        title="Identity verification"
        fill={fillsHostedFrame}
      />
    );
  };

  // Back handler for the old in-app capture steps (not used since the
  // hosted flow; see renderCustomKycStep).
  const getCustomKycBackHandler = () => {
    if (kycStep === KYC_STEP.START && usedFor === "popup") {
      return handleCustomKycCancel;
    }

    if (kycStep === KYC_STEP.SELECT_DOC) {
      return () => setKycStep(KYC_STEP.START);
    }

    if (kycStep === KYC_STEP.CAPTURE_FRONT) {
      return () => setKycStep(KYC_STEP.SELECT_DOC);
    }

    if (kycStep === KYC_STEP.CAPTURE_BACK) {
      return () => setKycStep(KYC_STEP.CAPTURE_FRONT);
    }

    if (kycStep === KYC_STEP.CONTINUE_ON_PHONE) {
      return handleMobileCancel;
    }

    return null;
  };

  // The Didit iframe is on screen (not loading, failed or processing).
  const showsHostedFrame =
    !kycInitiateLoading &&
    !kycInitiationFailed &&
    kycStep !== KYC_STEP.PROCESSING &&
    !!kycSessionUrl;
  // Inside the popup Modal the iframe keeps its standard height.
  const fillsHostedFrame = showsHostedFrame && usedFor !== "popup";

  const isProcessingStep =
    kycStep === KYC_STEP.UPLOADING ||
    kycStep === KYC_STEP.PROCESSING ||
    (kycStep === KYC_STEP.CONTINUE_ON_PHONE &&
      mobileSessionState === "SUBMITTED");

  // Old in-app capture screens (document select, upload / camera, phone
  // handoff). Not used since KYC moved to Didit's hosted flow.
  const renderCustomKycStep = () => {
    const availableDocs = getDocumentsForCountry(issuingCountry);

    if (
      captureSubMode === "camera-guide" &&
      kycStep === KYC_STEP.CAPTURE_BACK
    ) {
      return (
        <GuideScreen
          docType={docType}
          requiresBack={needsBackSide}
          frontCaptured={Boolean(frontImage)}
          onStart={() => setCaptureSubMode("camera")}
        />
      );
    }

    if (
      captureSubMode === "camera" &&
      (kycStep === KYC_STEP.CAPTURE_FRONT || kycStep === KYC_STEP.CAPTURE_BACK)
    ) {
      const isBack = kycStep === KYC_STEP.CAPTURE_BACK;
      return (
        <CaptureScreen
          side={isBack ? "back" : "front"}
          onCapture={
            isBack
              ? handleBackCapture
              : (image) => {
                  handleFrontCapture(image);
                  if (needsBackSide) {
                    setCaptureSubMode("camera-guide");
                  }
                }
          }
          onCancel={() => setCaptureSubMode(null)}
          reviewBeforeSubmit
        />
      );
    }

    switch (kycStep) {
      case KYC_STEP.START:
        return (
          <VerificationStartScreen
            onStart={handleStartVerification}
            isTenantCheck={isTenantCheck}
          />
        );

      case KYC_STEP.SELECT_DOC:
        return (
          <DocumentSelectScreen
            issuingCountry={issuingCountry}
            docType={docType}
            availableCountries={COUNTRIES}
            availableDocs={availableDocs}
            onCountryChange={handleCountryChange}
            onDocTypeChange={handleDocTypeChange}
            onContinue={handleDocTypeSelect}
            showRetryMessage={showDocumentRetryMessage}
            _t={_t}
          />
        );

      case KYC_STEP.CAPTURE_FRONT:
        return renderCaptureStep({
          title: getDocumentUploadTitle("front"),
          subtitle: getDocumentUploadSubtitle("front"),
          side: "front",
          onFileUpload: handleFileUploadFront,
          onContinueOnPhone: isMobileOrTablet
            ? undefined
            : handleCreateMobileSession,
          onTakePhoto: isMobileOrTablet
            ? () => setCaptureSubMode("camera")
            : undefined,
        });

      case KYC_STEP.CAPTURE_BACK:
        return renderCaptureStep({
          title: getDocumentUploadTitle("back"),
          subtitle: getDocumentUploadSubtitle("back"),
          side: "back",
          onFileUpload: handleFileUploadBack,
          onTakePhoto: isMobileOrTablet
            ? () => setCaptureSubMode("camera")
            : undefined,
        });

      case KYC_STEP.UPLOADING:
        return <ProcessingScreen />;

      case KYC_STEP.CONTINUE_ON_PHONE:
        if (mobileSessionState === "SUBMITTED") {
          return <ProcessingScreen />;
        }
        return (
          <ContinueOnPhoneScreen
            qrUrl={mobileSessionData?.qrUrl ?? ""}
            copyUrl={mobileSessionData?.copyUrl ?? ""}
            sessionState={mobileSessionState}
            onCancel={handleMobileCancel}
          />
        );

      case KYC_STEP.PROCESSING:
        return <ProcessingScreen />;

      default:
        return null;
    }
  };

  const renderKycInitiationFailed = () => (
    <div className={styles.customKycInitFailed} role="alert">
      <div className={styles.customKycInitFailedIcon}>
        <Icon name="warning" size={28} />
      </div>
      <h3>{_t("kycInitializationFailed")}</h3>
      <p>{_t("kycInitializationFailedDescription")}</p>
      <Button
        size="lg"
        block
        className={styles.customKycInitFailedButton}
        disabled={kycInitiateLoading}
        onClick={startHostedKyc}
      >
        {_t("retryInitialization")}
      </Button>
    </div>
  );

  // const customKycBackHandler = getCustomKycBackHandler();
  const customKycBackHandler =
    kycStep === KYC_STEP.PROCESSING ? null : handleCustomKycCancel;

  const handleWelcomeBack = () => {
    if (isGuarantor) {
      const url = new URL(window.location.href);
      url.searchParams.set("action", "edit");
      window.history.replaceState(
        {},
        document.title,
        `${url.pathname}${url.search}`
      );
    }

    formHandler.prev();
  };

  return (
    <>
      {customKycStarted ? (
        <KycFlowSurface
          popup={usedFor === "popup"}
          isGuarantor={isGuarantor}
          onClose={handleCustomKycCancel}
          fillFrame={fillsHostedFrame}
        >
          {!isProcessingStep && (
            <div className={styles.customKycModalToolbar}>
              {hideBackButton ? (
                <span />
              ) : customKycBackHandler ? (
                <Button
                  variant="ghost"
                  className={styles.backButton}
                  iconStart={<Icon name="chevronLeft" size={16} />}
                  onClick={customKycBackHandler}
                  aria-label={_t("back")}
                >
                  {_t("goBack")}
                </Button>
              ) : (
                <span className={styles.customKycToolbarSpacer} />
              )}
              <div className={styles.customKycToolbarRight}>
                {isKycSkippable && onSkipKyc && (
                  <Button variant="outline" wrap onClick={onSkipKyc}>
                    {_t("kycUsePreviousVerification")}
                  </Button>
                )}
                {usedFor === "popup" ? (
                  <Button
                    variant="ghost"
                    iconOnly
                    iconStart={<Icon name="close" />}
                    onClick={handleCustomKycCancel}
                    aria-label={_t("close")}
                  />
                ) : !isKycSkippable ? (
                  <span className={styles.customKycToolbarSpacer} />
                ) : null}
              </div>
            </div>
          )}
          <div
            className={`${styles.customKycContainer} ${
              fillsHostedFrame ? styles.customKycContainerFill : ""
            }`}
          >
            {kycInitiateLoading ? (
              <LoadingScreen message={_t("initializingKyc")} />
            ) : kycInitiationFailed ? (
              renderKycInitiationFailed()
            ) : (
              // renderCustomKycStep()
              renderHostedKycStep()
            )}
          </div>
        </KycFlowSurface>
      ) : !failedKyc ? (
        <KycFlowSurface
          popup={usedFor === "popup"}
          welcome
          isGuarantor={isGuarantor}
          onClose={() => setShowKycPopup(false)}
        >
          <div className={styles.authenticateYourIdHeader}>
            {hideBackButton ? (
              <span />
            ) : (
              <Button
                variant="ghost"
                className={styles.backButton}
                iconStart={<Icon name="chevronLeft" size={16} />}
                onClick={
                  showVerificationStart && isKycSkippable && onSkipKyc
                    ? () => setShowVerificationStart(false)
                    : formHandler.prev
                }
              >
                {_t("goBack")}
              </Button>
            )}
            <div className={styles.authenticateYourIdHeaderRight}>
              {(usedFor === "popup" || isGuarantor) && (
                <Button
                  variant="ghost"
                  iconOnly
                  iconStart={<Icon name="close" />}
                  onClick={
                    usedFor === "popup"
                      ? () => setShowKycPopup(false)
                      : () => navigate("/home")
                  }
                  aria-label={_t("close")}
                />
              )}
            </div>
          </div>
          {isKycSkippable && onSkipKyc && !showVerificationStart ? (
            <ExistingVerificationScreen
              isCompleted={user?.onfido_user_kyc?.server_status === "completed"}
              onContinue={onSkipKyc}
              onVerifyAgain={() => setShowVerificationStart(true)}
              isLoading={kycInitiateLoading}
            />
          ) : (
            <VerificationStartScreen
              onStart={startHostedKyc}
              isLoading={kycInitiateLoading}
              isTenantCheck={isTenantCheck}
            />
          )}
        </KycFlowSurface>
      ) : null}
      <KycVerificationPopup
        showPopup={kycVerificationPopup}
        onContinue={handleKycContinue}
        isGuarantor={isGuarantor}
      />
      <KycDocLoader isGuarantor={isGuarantor} showPopup={showKycLoading} />
      <VerificationFailedModal
        showPopup={failedKyc}
        isGuarantor={isGuarantor}
        heading={_t("could_not_verify_your_documents")}
        subheading={_t("you_can_try_again")}
        closeModalState={setFailedKyc}
        hideBackgroundImage={undefined}
        buttonText={undefined}
        setKycStep={setKycStep}
        workflowResult={failedWorkflowResult}
      />
    </>
  );
};

export default AuthenticateYourId;
